import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sensorsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft, Wifi, WifiOff, Wrench, Battery, Thermometer, Droplets,
  Wind, Gauge, Sun, Activity, Radio, Plus, Loader2,
} from 'lucide-react';
import { format, subHours } from 'date-fns';
import { fr } from 'date-fns/locale';

const TIME_RANGES = [
  { label: '24h', hours: 24 },
  { label: '7 jours', hours: 168 },
  { label: '30 jours', hours: 720 },
];

const sensorTypeConfig = {
  soil_moisture: { label: 'Humidité sol', icon: Droplets, unit: '%', color: '#3b82f6' },
  soil_temperature: { label: 'Température sol', icon: Thermometer, unit: '°C', color: '#f97316' },
  air_temperature: { label: 'Température air', icon: Thermometer, unit: '°C', color: '#ef4444' },
  humidity: { label: 'Humidité air', icon: Droplets, unit: '%', color: '#06b6d4' },
  weather_station: { label: 'Station météo', icon: Wind, unit: 'km/h', color: '#8b5cf6' },
  soil_ph: { label: 'pH sol', icon: Gauge, unit: '', color: '#10b981' },
  flow_meter: { label: 'Débitmètre', icon: Activity, unit: 'L/min', color: '#f59e0b' },
  light: { label: 'Luminosité', icon: Sun, unit: 'lux', color: '#eab308' },
};

const statusConfig = {
  online: { label: 'En ligne', icon: Wifi, color: 'bg-emerald-500 text-white' },
  offline: { label: 'Hors ligne', icon: WifiOff, color: 'bg-red-500 text-white' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'bg-amber-500 text-white' },
};

const SensorDetailPage = () => {
  const { sensorId } = useParams();
  const navigate = useNavigate();
  const [sensor, setSensor] = useState(null);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readingsLoading, setReadingsLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState(0);
  const [newValue, setNewValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchSensor = useCallback(async () => {
    try {
      const res = await sensorsApi.getById(sensorId);
      setSensor(res.data);
    } catch {
      toast.error('Capteur introuvable');
      navigate('/sensors');
    } finally {
      setLoading(false);
    }
  }, [sensorId, navigate]);

  const fetchReadings = useCallback(async (rangeIndex) => {
    setReadingsLoading(true);
    try {
      const hours = TIME_RANGES[rangeIndex].hours;
      const from_dt = subHours(new Date(), hours).toISOString();
      const res = await sensorsApi.getReadings(sensorId, { from_dt, limit: 500 });
      setReadings(res.data);
    } catch {
      toast.error('Erreur lors du chargement des mesures');
    } finally {
      setReadingsLoading(false);
    }
  }, [sensorId]);

  useEffect(() => {
    fetchSensor();
    fetchReadings(0);
  }, [fetchSensor, fetchReadings]);

  const handleRangeChange = (idx) => {
    setSelectedRange(idx);
    fetchReadings(idx);
  };

  const handleAddReading = async (e) => {
    e.preventDefault();
    const val = parseFloat(newValue);
    if (isNaN(val)) return;
    setSubmitting(true);
    try {
      const typeConf = sensorTypeConfig[sensor?.sensor_type];
      await sensorsApi.addReading(sensorId, { value: val, unit: typeConf?.unit || null });
      toast.success('Mesure ajoutée');
      setNewValue('');
      fetchSensor();
      fetchReadings(selectedRange);
    } catch {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setSubmitting(false);
    }
  };

  const formatXAxis = (value) => {
    if (!value) return '';
    const hours = TIME_RANGES[selectedRange].hours;
    try {
      if (hours <= 24) return format(new Date(value), 'HH:mm');
      return format(new Date(value), 'dd/MM HH:mm');
    } catch {
      return '';
    }
  };

  const formatTooltip = (value) => {
    const unit = sensorTypeConfig[sensor?.sensor_type]?.unit || '';
    return [`${value}${unit}`, 'Valeur'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const typeConf = sensorTypeConfig[sensor?.sensor_type] || { label: sensor?.sensor_type, icon: Radio, unit: '', color: '#6b7280' };
  const TypeIcon = typeConf.icon;
  const statusConf = statusConfig[sensor?.status] || statusConfig.offline;
  const StatusIcon = statusConf.icon;

  const chartData = readings.map((r) => ({
    measured_at: r.measured_at,
    value: r.value,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sensors')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <TypeIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{sensor?.name}</h1>
            <p className="text-sm text-muted-foreground">{typeConf.label}</p>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Statut</p>
            <Badge className={`${statusConf.color} flex items-center gap-1 w-fit`}>
              <StatusIcon className="h-3 w-3" />
              {statusConf.label}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Dernière valeur</p>
            <p className="text-xl font-bold">
              {sensor?.last_reading != null ? `${sensor.last_reading}${typeConf.unit}` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Batterie</p>
            {sensor?.battery_level != null ? (
              <div className="flex items-center gap-2">
                <Battery className={`h-4 w-4 ${sensor.battery_level > 50 ? 'text-emerald-500' : sensor.battery_level > 20 ? 'text-amber-500' : 'text-red-500'}`} />
                <span className="text-xl font-bold">{sensor.battery_level}%</span>
              </div>
            ) : <span className="text-muted-foreground">—</span>}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Mesures chargées</p>
            <p className="text-xl font-bold">{readings.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle>Historique des mesures</CardTitle>
            <div className="flex gap-2">
              {TIME_RANGES.map((r, i) => (
                <Button
                  key={r.label}
                  variant={selectedRange === i ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleRangeChange(i)}
                >
                  {r.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {readingsLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground">
              Aucune mesure sur cette période
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={typeConf.color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={typeConf.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="measured_at" tickFormatter={formatXAxis} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit={typeConf.unit} />
                <Tooltip
                  formatter={formatTooltip}
                  labelFormatter={(v) => { try { return format(new Date(v), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return v; } }}
                  contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={typeConf.color}
                  strokeWidth={2}
                  fill="url(#colorValue)"
                  dot={chartData.length < 50}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Add manual reading */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4" />
            Ajouter une mesure manuelle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddReading} className="flex items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="reading-value">
                Valeur ({typeConf.unit || 'unité'})
              </Label>
              <Input
                id="reading-value"
                type="number"
                step="any"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={`ex: 45${typeConf.unit}`}
                className="w-48"
                required
              />
            </div>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SensorDetailPage;
